/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, January 4th 2023, 2:56:35 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { OSAPIForm } from "ui-widgets/forms";
import { Team, TaskTemplate } from "lib/schemas";
import GroupsIcon from "@mui/icons-material/Groups";
import Grid from "@mui/material/Grid";
import { useTeamAPIForm } from "teams/services/TeamsAPI";
import { IconText, StyledOSDialog, FormSubmitBubblingStopper, EmphasisText } from "ui-widgets";
import { ListButtonAutoComplete } from "ui-widgets/select/ListButtonAutoComplete";
import { TeamSelectorInput } from "teams/components/TeamSelectorInputs";
import { TASK_ACCESS_PERMISSION_WIDGETS, getTaskAccessPermissionsWidgets } from "./TaskTypeWidgets";

interface TaskAccessFormProps {
  open: boolean;
  handleFormClose: () => void;
  chosenTeam: Team;
  setChosenTeam: React.Dispatch<React.SetStateAction<Team>>;
  taskTemplate: TaskTemplate;
  onUpdateTeamsPermissions: (team: Team) => void;
}

export const TaskAccessForm = ({
  open,
  handleFormClose,
  chosenTeam,
  setChosenTeam,
  onUpdateTeamsPermissions,
  taskTemplate,
}: TaskAccessFormProps) => {
  const teamName = chosenTeam?.team_name;
  const taskTitle = taskTemplate?.title;
  const taskExecutePermissionId = taskTemplate?.execute_permission;
  const taskAssignPermissionId = taskTemplate?.assign_permission;
  const taskSupervisePermissionId = taskTemplate?.supervise_permission;
  const taskUndoPermissionId = taskTemplate?.undo_permission;
  const taskPermissionsWidgets = getTaskAccessPermissionsWidgets(taskTemplate);

  const useTeamAPIFormReturn = useTeamAPIForm({ team: chosenTeam, updateSelectedTeam: onUpdateTeamsPermissions });

  const {
    useFormReturn: { setValue: setTeamFormValue, watch: watchTeamFormValues },
  } = useTeamAPIFormReturn;

  const teamPermissions = (watchTeamFormValues<keyof Team>("permissions") as number[]) ?? [];

  const taskPermissionIds = [
    taskExecutePermissionId,
    taskAssignPermissionId,
    taskUndoPermissionId,
    taskSupervisePermissionId,
  ];

  const chosenPermissionIds = teamPermissions.filter((teamPermissionId) =>
    taskPermissionIds.includes(teamPermissionId)
  );

  const chosenPermissions = taskPermissionsWidgets.filter(
    (TaskPermission) => chosenPermissionIds.includes(TaskPermission.permissionId) && TaskPermission.permissionId
  );

  const handleChangeValue = (selectedPermissions: TASK_ACCESS_PERMISSION_WIDGETS[]) => {
    const selectedPermissionIds = selectedPermissions.map((permissionDesc) => permissionDesc.permissionId);

    const teamPermissionIdsWithoutCurrentTaskPermissions = teamPermissions.filter(
      (tp) => !taskPermissionIds.includes(tp)
    );

    setTeamFormValue("permissions", [...teamPermissionIdsWithoutCurrentTaskPermissions, ...selectedPermissionIds]);
  };

  return (
    <FormSubmitBubblingStopper>
      <StyledOSDialog
        popUpIcon={<GroupsIcon color="secondary" />}
        popUpTitle={
          <>
            Manage{" "}
            {teamName && (
              <>
                {" "}
                <EmphasisText>{teamName}'s</EmphasisText>
              </>
            )}{" "}
            Access to <EmphasisText>{taskTitle}</EmphasisText>
          </>
        }
        open={open}
        onClose={handleFormClose}
        className="os-task-team-access-modal"
      >
        <Grid container spacing={2}>
          <Grid item xs={1} md={12}>
            <OSAPIForm
              useAPIFormReturn={useTeamAPIFormReturn}
              submitButtonText="Save Changes"
              fieldsToShow={["permissions"]}
              showFeedbackAlerts={false}
            >
              <TeamSelectorInput chosenTeam={chosenTeam} setChosenTeam={setChosenTeam} />

              <ListButtonAutoComplete
                options={taskPermissionsWidgets.filter((tp) => tp.permissionId !== null)}
                getOptionLabel={(option) => option.title ?? ""} // For the TextField
                getPrimaryOptionLabel={(option) => <IconText icon={option.icon} textComponent={option.title} />}
                getSecondaryOptionLabel={(option) => option.description}
                disableClearable
                disableCloseOnBlur
                textFieldProps={{ label: "Access Levels" }}
                multiple={true as any}
                onChangeCallBack={handleChangeValue}
                isOptionEqualToValue={(option, value) => option.permissionId === value.permissionId}
                value={(chosenPermissions as any) ?? ([] as any[])} // Ensuring the AutoComplete component is controlled by providing a default value that is not undefined or null. React MUI Autocomplete assumes the component is uncontrolled if the value on the first render is undefined. This is not such a good design choice if you ask me.
              />
            </OSAPIForm>
          </Grid>
        </Grid>
      </StyledOSDialog>
    </FormSubmitBubblingStopper>
  );
};
